<template>
    <div class="flex flex-col gap-4">
        <!-- Loading State -->
        <template v-if="loading">
            <div v-for="i in 3" :key="i" class="flex flex-col gap-3">
                <Skeleton height="60px" borderRadius="12px" />
                <Skeleton height="120px" borderRadius="12px" />
            </div>
        </template>

        <!-- Timeline View -->
        <template v-else-if="resources.length > 0">
            <div v-for="resource in resources" :key="resource.id" class="flex flex-col gap-3">
                <!-- Resource Card -->
                <Card class="shadow-sm border cursor-pointer hover:shadow-md transition-shadow" @click="$emit('openSchedule', resource.id)">
                    <template #content>
                        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div class="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                <div :class="['p-2 sm:p-3 rounded-lg shrink-0', getResourceStatusClass(resource).bgClass]">
                                    <i :class="['text-lg sm:text-xl', getResourceStatusClass(resource).iconClass, 'fa-solid fa-wrench']"></i>
                                </div>
                                <div>
                                    <div class="flex flex-col min-w-0">
                                        <h3 class="font-semibold text-base sm:text-lg mb-0 truncate">{{ resource.name }} ({{ resource.type.name }})</h3>
                                    </div>
                                    <Tag :severity="getResourceStatusClass(resource).severity" :value="getResourceStatusText(resource)" />
                                </div>
                            </div>
                            <Button icon="fa-solid fa-cog" :label="t('maintenance.changeSchedule')" class="w-full sm:w-auto shrink-0" @click.stop="$emit('openSchedule', resource.id)" />
                        </div>
                        <!-- Timeline -->
                        <div v-if="resource.timespans.length > 0">
                            <Divider />
                            <!-- Timeline Header -->
                            <div class="flex justify-between items-center mb-4 text-gray-600 font-medium">
                                <span>{{ timelineStart }}</span>
                                <span>{{ timelineEnd }}</span>
                            </div>

                            <!-- Timeline Bar -->
                            <div class="relative h-8 bg-gray-200 rounded-full overflow-hidden mb-4">
                                <!-- Today Indicator -->
                                <div class="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20" :style="{ left: todayPosition }">
                                    <div class="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full"></div>
                                </div>

                                <!-- Maintenance Blocks -->
                                <div
                                    v-for="(timespan, idx) in resource.timespans"
                                    :key="idx"
                                    class="absolute top-1 bottom-1 rounded-full transition-all hover:scale-y-110 cursor-pointer"
                                    :class="getMaintenanceColor(timespan)"
                                    :style="getMaintenancePosition(timespan)"
                                    :title="`${formatDateRange(timespan)} - ${formatTimeRange(timespan)}`"
                                    @mouseenter="onMaintenanceHover(resource.id, idx)"
                                    @mouseleave="onMaintenanceLeave(resource.id)"
                                ></div>
                            </div>

                            <!-- Maintenance Cards -->
                            <div class="flex flex-col gap-2">
                                <div
                                    v-for="(timespan, idx) in resource.timespans"
                                    :key="idx"
                                    class="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border transition-all"
                                    :class="[getMaintenanceBorderClass(timespan), isMaintenanceHighlighted(resource.id, idx) ? 'ring-2 ring-blue-500 shadow-md' : '']"
                                    @mouseenter="onMaintenanceHover(resource.id, idx)"
                                    @mouseleave="onMaintenanceLeave(resource.id)"
                                >
                                    <div :class="['p-2 rounded-lg shrink-0', getStatusClass(timespan).bgClass]">
                                        <i :class="[getStatusClass(timespan).iconClass, 'fa-solid fa-tools']"></i>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <div class="flex flex-wrap items-center gap-2 mb-1">
                                            <span class="font-semibold truncate">{{ timespan.title || t('maintenance.untitled') }}</span>
                                            <Tag :value="getStatusLabel(timespan)" size="small" :class="getStatusClass(timespan).badgeClass" />
                                            <Tag size="small" :severity="isPartial(timespan) ? 'warn' : 'secondary'" :value="modeLabel(timespan)" />
                                        </div>
                                        <p class="text-gray-600 text-sm sm:text-base">{{ formatDateRange(timespan) }} • {{ formatTimeRange(timespan) }}</p>
                                        <p v-if="timespan.description" class="text-gray-500 mt-1 line-clamp-2 text-sm">
                                            {{ timespan.description }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
        </template>

        <!-- Empty State -->
        <div v-else class="flex flex-col items-center justify-center py-12 text-gray-500">
            <i class="fa-solid fa-wrench text-5xl mb-4 text-gray-400"></i>
            <h3 class="text-lg font-semibold mb-2">{{ t('maintenance.noMaintenances') }}</h3>
            <p>{{ t('maintenance.noMaintenancesDesc') }}</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ResourceWithTimespans } from '@/composables/useMaintenancesPage';
import { BlockingMode, type MaintenanceTimeSpan } from '@/lib/types/maintenance';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
    loading: boolean;
    resources: ResourceWithTimespans[];
    timelineStart: string;
    timelineEnd: string;
    todayPosition: string;
    getResourceStatusClass: (resource: ResourceWithTimespans) => { bgClass: string; iconClass: string; severity: 'warn' | 'info' | 'success' };
    getResourceStatusText: (resource: ResourceWithTimespans) => string;
    getMaintenanceColor: (timespan: MaintenanceTimeSpan) => string;
    getMaintenanceBorderClass: (timespan: MaintenanceTimeSpan) => string;
    getMaintenancePosition: (timespan: MaintenanceTimeSpan) => { left: string; width: string };
    getStatusClass: (timespan: MaintenanceTimeSpan) => { bgClass: string; iconClass: string; badgeClass: string };
    getStatusLabel: (timespan: MaintenanceTimeSpan) => string;
    formatDateRange: (timespan: MaintenanceTimeSpan) => string;
    formatTimeRange: (timespan: MaintenanceTimeSpan) => string;
}

interface Emits {
    (e: 'openSchedule', resourceId: string): void;
}

defineProps<Props>();
defineEmits<Emits>();
const { t } = useI18n();

// Track hovered maintenance per resource
const hoveredMaintenance = ref<Record<string, number | null>>({});

function onMaintenanceHover(resourceId: string, index: number) {
    hoveredMaintenance.value[resourceId] = index;
}

function onMaintenanceLeave(resourceId: string) {
    hoveredMaintenance.value[resourceId] = null;
}

function isMaintenanceHighlighted(resourceId: string, index: number): boolean {
    return hoveredMaintenance.value[resourceId] === index;
}

// Local rather than injected through Props: every other helper on this component depends on page-level state
// (the date range, the timeline bounds), while these two depend only on the timespan the template already holds.
function isPartial(timespan: MaintenanceTimeSpan): boolean {
    return timespan.blockingMode === BlockingMode.PartiallyBlocking;
}

function modeLabel(timespan: MaintenanceTimeSpan): string {
    // `reducedCapacity != null` is the partial test everywhere else in the phase (CalendarEventPreviewCard,
    // SlotTimelinePreview, TimelineBlockTooltip): the mode and the cap are only meaningful read together, and
    // vue-i18n interpolates a null as the empty string, which would render "Capacita ridotta: " silently.
    if (isPartial(timespan) && timespan.reducedCapacity != null) {
        return t('maintenance.blockingMode.partial', { capacity: timespan.reducedCapacity });
    }
    return t('maintenance.blockingMode.fully');
}
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
